import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { BASE_URL } from "../config";
import { getAuditTrailData, getFieldsIncluded } from "../util/getAuditTrailData";
import { uploadJsonToS3 } from "../util/AppUtils";

const baseUrl = BASE_URL + "procedure-revision-status";

const parseRevision = (rev) => {
  if (!rev) return null;

  // Check for special characters only
  const hasSpecialChars = /[@#$%!^&*]/.test(rev);
  if (hasSpecialChars) {
    return {
      base: "",
      type: "special_chars",
      minorNum: 0,
      amendChar: "",
      original: rev,
    };
  }

  // Standalone dash
  if (rev === "-") {
    return {
      base: "",
      type: "special",
      minorNum: 0,
      amendChar: "",
      original: rev,
    };
  }

  // e.g., A-, AB-, ZY-
  if (/^[A-Z]{1,3}-$/.test(rev)) {
    return {
      base: rev.slice(0, -1),
      type: "major",
      minorNum: 0,
      amendChar: "",
      original: rev,
    };
  }

  // e.g., A1-a, B2-c
  const amendMatchWithHyphen = rev.match(/^([A-Z]{1,3})(\d+)-([a-z]+)$/);
  if (amendMatchWithHyphen) {
    const [, base, num, amendChar] = amendMatchWithHyphen;
    return {
      base,
      type: "amend",
      minorNum: parseInt(num),
      amendChar,
      original: rev,
    };
  }

  // ✅ NEW: e.g., A-a, AB-b (amendment without minor number)
  const amendMinorlessMatch = rev.match(/^([A-Z]{1,3})-([a-z]+)$/);
  if (amendMinorlessMatch) {
    const [, base, amendChar] = amendMinorlessMatch;
    return {
      base,
      type: "amend_minorless",
      minorNum: 0,
      amendChar,
      original: rev,
    };
  }

  // e.g., A1, A2a
  const minorMatch = rev.match(/^([A-Z]{1,3})(\d+)([a-z]*)$/);
  if (minorMatch) {
    const [, base, num, amendChar] = minorMatch;
    return {
      base,
      type: amendChar ? "amend" : "minor",
      minorNum: parseInt(num),
      amendChar: amendChar || "",
      original: rev,
    };
  }

  return null;
};

// Helper function to compare revisions and determine if one is "below" another
const isRevisionBelow = (currentRev, compareRev) => {
  const currentParsed = parseRevision(currentRev);
  const compareParsed = parseRevision(compareRev);

  if (!currentParsed || !compareParsed) return false;

  // 1. Revisions with different base letters are NOT comparable
  if (currentParsed.base !== compareParsed.base) {
    return false;
  }

  // 2. Compare minor numbers
  // "Below" means it comes AFTER the current revision in normal sort order
  // Example: B3 is below B2c, and B2d is below B2c
  if (compareParsed.minorNum > currentParsed.minorNum) {
    return true;
  }

  // 3. If same minor number, compare amendment characters
  if (compareParsed.minorNum === currentParsed.minorNum) {
    // Minor (no amend) comes before amendments: B2 is above B2a
    if (!currentParsed.amendChar && compareParsed.amendChar) {
      return true;
    }
    if (compareParsed.amendChar && currentParsed.amendChar) {
      return compareParsed.amendChar > currentParsed.amendChar;
    }
  }

  return false;
};

// 📌 Create Procedure Revision (POST)
export const createProcedureRevisionStatus = createAsyncThunk(
  "procedureRevisionStatus/create",
  async (revisionData, { rejectWithValue }) => {
    try {
      const password = revisionData?.audit_trail_data?.hash;
      const isPasswordRequired =
        revisionData?.audit_trail_data?.is_password_required !== false;
      const revDataCopy = { ...revisionData };
      delete revDataCopy.audit_trail_data?.hash;

      const auditData = await getAuditTrailData({
        ...revisionData?.audit_trail_data,
      });
      
      const { required_by,  rev, partNumber, item_code_type} = JSON.parse(JSON.stringify(revisionData?.audit_trail_data.version_content))

      // Exclude signature from version_content if it exists
      const { signature: _, ...versionContentWithoutSignature } =
        revisionData?.audit_trail_data.version_content || {};
      

        const revisionNodesSnapshot = await uploadJsonToS3({
        ...revisionData?.audit_trail_data.version_content,
        ...versionContentWithoutSignature,
        password_bullets: password ? password.length : 0,
        current_status: revisionData.status_changed_to,
        revisionNodes:revisionData?.audit_trail_data.version_content.revisionNodes,
        ...getFieldsIncluded()
      }, revisionData?.audit_trail_data.version_content.partNumber,revisionData?.audit_trail_data?.version_content?.rev+"revision_action_preview",true);

      const payload = {
        ...revDataCopy,
        ...(isPasswordRequired && password ? { password: password } : {}),
        audit_trail_data: {
          ...auditData,
          ...revisionData.audit_trail_data,
          version_content: {
            action_to_show:revisionData?.audit_trail_data?.version_content?.action_to_show,
            status_changes_preview:true,
            rev,
            revision:rev,
            status_changed_to:revisionData.status_changed_to,
            status:revisionData.status_changed_to
          },
          required_by,
          rev,
          partNumber,
          item_code_type,
          new_content_snapshot:revisionNodesSnapshot  
        },
      };

      // ✅ Check for amendment type AND release status 
      const revisionToCheck =
        payload.rev || payload.audit_trail_data.version_content.rev;
      const parsed = parseRevision(revisionToCheck);
      const isAmendment =
        parsed?.type === "amend" || parsed?.type === "amend_minorless";
      const isReleasing =
        payload.status_changed_to?.toLowerCase() === "released";

      let url = `${baseUrl}/create`;

      // Only call amendment-status API for amended revisions being released
      if (isAmendment && isReleasing) {
        const currentRevision = payload.audit_trail_data.version_content.rev;

        // Check if there's any released revision "below" the current amendment
        let hasReleasedRevisionBelow = false;

        payload.revisionDataList?.forEach((rev, index) => {
          const latestStatus = rev.status_changes?.find((sc) => sc.is_latest);
          const isReleased =
            latestStatus?.status_changed_to?.toLowerCase() === "released";
          const isBelow = isRevisionBelow(currentRevision, rev.rev);

          if (isReleased && isBelow) {
            hasReleasedRevisionBelow = true;
          }
        });

        // ✅ Set TRUE if a released revision is found below
        // Backend expects this flag to reflect whether a *released* revision exists below the current one.
        // Do NOT invert it.
        payload.is_amendment_after_release = hasReleasedRevisionBelow;
        url = `${baseUrl}/amendment-status`;
      }
      const response = await axios.post(url, payload);
      // Check if response indicates failure (success: false)
      if (response.data && response.data.success === false) {
        return rejectWithValue(
          response.data.message || response.data.error || "Operation failed"
        );
      }
      return response.data;
    } catch (error) {
      // Handle error response structure: {success: false, message: "Incorrect password"}
      let errorMessage = "Error creating revision";

      // Check if error.response.data is the response object directly
      if (error?.response?.data) {
        const errorData = error.response.data;
        // Check for {success: false, message: "..."} structure
        if (errorData.success === false && errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.error) {
          errorMessage = errorData.error;
        }
      } else if (error?.message) {
        errorMessage = error.message;
      }

      return rejectWithValue(errorMessage);
    }
  }
);

// 📌 Update Procedure Revision (PUT)
export const updateProcedureRevisionStatus = createAsyncThunk(
  "procedureRevisionStatus/update",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await axios.put(`${baseUrl}/update/${id}`, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error updating revision");
    }
  }
);

// 📌 Get All Procedure Revisions (GET)
export const getAllProcedureRevisionStatus = createAsyncThunk(
  "procedureRevisionStatus/getAll",
  async ({ limit = 10, offset = 0 }, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${baseUrl}/get-all?limit=${limit}&offset=${offset}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Error fetching revisions"
      );
    }
  }
);

// 📌 Delete Procedure Revision (DELETE)
export const deleteProcedureRevisionStatus = createAsyncThunk(
  "procedureRevisionStatus/delete",
  async (id, { rejectWithValue }) => {
    try {
      await axios.delete(`${baseUrl}/delete/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error deleting revision");
    }
  }
);

// 🛠 Procedure Revision Status Slice
const procedureRevisionStatusSlice = createSlice({
  name: "procedureRevisionStatus",
  initialState: {
    revisions: [],
    loading: false,
    error: null,
    revisionId: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(createProcedureRevisionStatus.pending, (state) => {
        state.loading = true;
      })
      .addCase(createProcedureRevisionStatus.fulfilled, (state, action) => {
        state.loading = false;
        // state.revisionId =
        //   action.payload.results.tableOfProcedures.procedure_revision_ref;
        // state.revisions.push(action.payload);
      })
      .addCase(createProcedureRevisionStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload.error || "An error occurred";
      })
      .addCase(updateProcedureRevisionStatus.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateProcedureRevisionStatus.fulfilled, (state, action) => {
        state.loading = false;
        state.revisions = state.revisions.map((rev) =>
          rev.id === action.payload.id ? action.payload : rev
        );
      })
      .addCase(updateProcedureRevisionStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload.error || "An error occurred";
      })
      .addCase(getAllProcedureRevisionStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllProcedureRevisionStatus.fulfilled, (state, action) => {
        state.loading = false;
        state.revisions = action.payload.results;
      })
      .addCase(getAllProcedureRevisionStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload.error || "An error occurred";
      })
      .addCase(deleteProcedureRevisionStatus.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteProcedureRevisionStatus.fulfilled, (state, action) => {
        state.loading = false;
        state.revisions = state.revisions.filter(
          (rev) => rev.id !== action.payload
        );
      })
      .addCase(deleteProcedureRevisionStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload.error || "An error occurred";
      });
  },
});

export default procedureRevisionStatusSlice.reducer;
